/**
 * Persist first-touch UTM and Meta click ids so they survive in-app
 * navigation (Instagram → /start → /join → /payment).
 */

export const ATTRIBUTION_STORAGE_KEY = 'lm_attribution';

export const ATTRIBUTION_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'fbclid',
] as const;

export type AttributionParamKey = (typeof ATTRIBUTION_PARAM_KEYS)[number];
export type AttributionParams = Partial<Record<AttributionParamKey, string>>;

export function parseAttributionParams(
  search: string | URLSearchParams | null | undefined
): AttributionParams {
  const params =
    typeof search === 'string'
      ? new URLSearchParams(search.startsWith('?') ? search : `?${search}`)
      : search ?? new URLSearchParams();

  const out: AttributionParams = {};
  for (const key of ATTRIBUTION_PARAM_KEYS) {
    const value = params.get(key)?.trim();
    if (value) out[key] = value;
  }
  return out;
}

export function mergeAttribution(
  current: AttributionParams | null | undefined,
  incoming: AttributionParams
): AttributionParams {
  return { ...(current ?? {}), ...incoming };
}

export function readStoredAttribution(): AttributionParams {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as AttributionParams;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function persistAttributionFromLocation(
  search?: string | URLSearchParams
): AttributionParams {
  if (typeof window === 'undefined') return {};
  const incoming = parseAttributionParams(search ?? window.location.search);
  const merged = mergeAttribution(readStoredAttribution(), incoming);
  try {
    if (Object.keys(merged).length > 0) {
      window.sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(merged));
    }
  } catch {
    // Private mode / quota — attribution still applies to this URL.
  }
  return mergeAttribution(merged, incoming);
}

export function getAttributionProperties(): AttributionParams {
  if (typeof window === 'undefined') return {};
  return mergeAttribution(readStoredAttribution(), parseAttributionParams(window.location.search));
}

export function appendAttributionToUrl(pathOrUrl: string, attribution?: AttributionParams): string {
  const params = attribution ?? getAttributionProperties();
  const url = new URL(pathOrUrl, 'https://www.liormurphy.com');
  for (const key of ATTRIBUTION_PARAM_KEYS) {
    const value = params[key];
    if (value && !url.searchParams.get(key)) {
      url.searchParams.set(key, value);
    }
  }
  return `${url.pathname}${url.search}${url.hash}`;
}

/** Meta fbc cookie: fb.1.{unix_ms}.{fbclid} */
export function buildFbcFromFbclid(
  fbclid: string | undefined,
  existingFbc?: string,
  nowMs: number = Date.now()
): string | undefined {
  if (existingFbc?.trim()) return existingFbc.trim();
  if (!fbclid?.trim()) return undefined;
  return `fb.1.${nowMs}.${fbclid.trim()}`;
}
